import React, { useEffect, useState } from "react";
import { getToken } from "../../utils/getToken";

const baseURL = process.env.REACT_APP_BASE_URL || 'https://devapi.quickcare.ng/admin';

const AllPatient = () => {
    const [patients, setPatients] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPatients, setTotalPatients] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const pageSize = 10;

    const fetchPatients = async (pageNumber) => {
        setLoading(true);
        setError(null);
        
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");

        const token = getToken();
        if (token) {
            myHeaders.append("Authorization", `Bearer ${token}`);
        } else {
            setError("Authentication failed. Please login again.");
            setLoading(false);
            return;
        }

        const requestOptions = {
            method: "GET",
            headers: myHeaders,
            redirect: "follow",
        };

        try {
            const response = await fetch(
                `${baseURL}/patients/?page=${pageNumber}&page_size=${pageSize}&search_term=`,
                requestOptions
            );
            const result = await response.json();
            if (result.success) {
                setPatients(result.data);
                setTotalPatients(result.total);
            } else {
                setError("Failed to fetch patients data");
            }
        } catch (error) {
            console.error("Error fetching patients:", error);
            setError("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPatients(page);
    }, [page]);

    const handleNextPage = () => {
        if (page < Math.ceil(totalPatients / pageSize)) {
            setPage((prevPage) => prevPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (page > 1) {
            setPage((prevPage) => prevPage - 1);
        }
    };

    // Get status color
    const getStatusColor = (isSubscribed) => {
        return isSubscribed 
            ? 'text-green-700 bg-green-100' 
            : 'text-gray-500 bg-gray-100';
    };

    // Get country flag
    const getCountryFlag = (country) => {
        const flags = {
            'kenya': '🇰🇪',
            'nigeria': '🇳🇬',
            'ghana': '🇬🇭',
            'south africa': '🇿🇦',
            'tanzania': '🇹🇿',
            'uganda': '🇺🇬'
        };
        return flags[country?.toLowerCase()] || '🌍';
    };

    if (loading) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#069125]"></div>
                    <p className="text-sm text-gray-500 mt-4">Loading patients...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <span className="text-4xl mb-3">⚠️</span>
                    <h3 className="text-lg font-semibold text-gray-700">Error Loading Patients</h3>
                    <p className="text-sm text-gray-500 mt-1">{error}</p>
                    <button 
                        onClick={() => fetchPatients(page)}
                        className="mt-4 px-6 py-2 bg-[#069125] text-white text-sm font-medium rounded-xl hover:bg-[#057a1f] transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 lg:p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full"></div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">All Patients</h2>
                        <p className="text-xs text-gray-400">
                            Showing {patients.length} of {totalPatients} patients
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-2 sm:mt-0">
                    <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
                        Page {page} of {Math.ceil(totalPatients / pageSize)}
                    </span>
                </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100/80 border-b border-gray-200">
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ID</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Patient</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Country</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Registered</th>
                        </tr>
                    </thead>
                    <tbody>
                        {patients.length > 0 ? (
                            patients.map((patient, index) => (
                                <tr 
                                    key={patient.id}
                                    className={`border-b border-gray-50 hover:bg-green-50/30 transition-colors duration-150 ${
                                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                                    }`}
                                >
                                    <td className="px-4 py-3 text-sm text-gray-600">#{patient.id}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#069125] to-[#057a1f] flex items-center justify-center text-white text-sm font-bold">
                                                {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
                                            </div>
                                            <span className="text-sm font-medium text-gray-800">
                                                {patient.first_name} {patient.last_name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{patient.email}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{patient.phone_number}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                        <span className="flex items-center gap-1">
                                            {getCountryFlag(patient.country)}
                                            {patient.country}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(patient.is_subscribed)}`}>
                                            {patient.is_subscribed ? '✅ Active' : '❌ Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        {new Date(patient.registration_date).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={7} className="px-4 py-12 text-center">
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="text-3xl">📭</span>
                                        <span className="text-sm text-gray-500">No patients found</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-100">
                {patients.length > 0 ? (
                    patients.map((patient) => (
                        <div key={patient.id} className="p-4 hover:bg-green-50/30 transition-colors">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#069125] to-[#057a1f] flex items-center justify-center text-white text-lg font-bold">
                                        {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">
                                            {patient.first_name} {patient.last_name}
                                        </h3>
                                        <span className="text-xs text-gray-400">ID: #{patient.id}</span>
                                    </div>
                                </div>
                                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(patient.is_subscribed)}`}>
                                    {patient.is_subscribed ? '✅ Active' : '❌ Inactive'}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-2">
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-500">Email</p>
                                    <p className="text-sm font-medium text-gray-700 truncate">{patient.email}</p>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-500">Phone</p>
                                    <p className="text-sm font-medium text-gray-700">{patient.phone_number}</p>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-500">Country</p>
                                    <p className="text-sm font-medium text-gray-700 flex items-center gap-1">
                                        {getCountryFlag(patient.country)} {patient.country}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-500">Registered</p>
                                    <p className="text-sm font-medium text-gray-700">
                                        {new Date(patient.registration_date).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="p-8 text-center">
                        <span className="text-3xl block">📭</span>
                        <span className="text-sm text-gray-500 block mt-2">No patients found</span>
                    </div>
                )}
            </div>

            {/* Pagination */}
            <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="text-sm text-gray-500">
                    Showing <span className="font-semibold text-gray-700">{patients.length}</span> of{' '}
                    <span className="font-semibold text-gray-700">{totalPatients}</span> patients
                </div>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={handlePreviousPage}
                        className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            page === 1 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-[#069125] text-white hover:bg-[#057a1f] hover:shadow-md'
                        }`}
                        disabled={page === 1}
                    >
                        ◀ Previous
                    </button>
                    <span className="text-sm text-gray-500 px-2">
                        {page} / {Math.ceil(totalPatients / pageSize) || 1}
                    </span>
                    <button 
                        onClick={handleNextPage}
                        className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            page >= Math.ceil(totalPatients / pageSize) 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-[#069125] text-white hover:bg-[#057a1f] hover:shadow-md'
                        }`}
                        disabled={page >= Math.ceil(totalPatients / pageSize)}
                    >
                        Next ▶
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AllPatient;